// [eyecue-codemap:4P2dZihfnX8]
/* eslint-disable  @typescript-eslint/no-explicit-any */
/* eslint-disable  no-console */

import { GraphQLClient } from "graphql-request";
import React from "react";
import SessionClient from "util/SessionClient";
import {
  ClaimDesignDocument,
  ClaimDesignMutation,
  ClaimNextDesignDocument,
  ClaimNextDesignMutation,
  DesignRequestFindManyWhereInput,
  GetQueueDocument,
  GetQueueQuery,
  QueueCountDocument,
  QueueCountQuery,
  QueueInput,
  RequestRevisionDocument,
  RequestRevisionMutation,
  RequestRevisionMutationVariables,
  UpdatePriorityDocument,
  UpdatePriorityMutation,
  UpdatePriorityMutationVariables,
} from "./client/graphql";

const visionErrorMessage = async (response: Response): Promise<string | undefined> => {
  try {
    const body = (await response.json()) as { message?: string | string[] };
    return Array.isArray(body.message) ? body.message.join(", ") : body.message;
  } catch {
    return undefined;
  }
};

export class VisionClient {
  public constructor(noisy = false) {
    this.noisy = noisy;
    this.getGraphQLClient = this.getGraphQLClient.bind(this);
    this.getQueue = this.getQueue.bind(this);
    this.queueCount = this.queueCount.bind(this);
    this.claimDesign = this.claimDesign.bind(this);
    this.claimNextDesign = this.claimNextDesign.bind(this);
    this.updatePriority = this.updatePriority.bind(this);
    this.requestRevision = this.requestRevision.bind(this);
    this.unassignDesignRequest = this.unassignDesignRequest.bind(this);
    this.log = this.log.bind(this);
  }

  private client: GraphQLClient | undefined;
  private noisy: boolean;
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  private log(...args: any[]) {
    if (this.noisy) {
      /* eslint-disable-next-line no-console */
      console.debug(...args);
    }
  }

  public getGraphQLClient(): GraphQLClient {
    const { jwt } = new SessionClient();
    this.log({ jwt });
    if (this.client) {
      this.log("client exists, setting header");
      this.client.setHeader("Authorization", `Bearer ${jwt}`);
      return this.client;
    }
    this.log("client does not exist, creating new client");
    this.client = new GraphQLClient(`${process.env.REACT_APP_VISION_API_URL}/graphql`, {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    });
    return this.client;
  }

  private async request<T>(document: any, variables?: any): Promise<T> {
    const client = this.getGraphQLClient();
    try {
      return await client.request<T>(document, variables);
    } catch (e) {
      const err = e as {
        response?: {
          status?: number;
          errors?: Array<{ message?: string; extensions?: { code?: string } }>;
          data?: { error?: { message?: string } };
        };
      };
      if (err?.response?.status === 403) {
        const bodyMessage = err.response.data?.error?.message;
        const gqlMessage = err.response.errors?.[0]?.message;
        const gqlCode = err.response.errors?.[0]?.extensions?.code;
        if (
          bodyMessage === "email_not_verified" ||
          gqlMessage === "email_not_verified" ||
          gqlCode === "email_not_verified"
        ) {
          const current = window.location.pathname;
          if (!current.startsWith("/verify-email")) {
            window.location.href = "/verify-email-required";
          }
        }
      }
      throw e;
    }
  }

  public async getQueue(queueInput: QueueInput): Promise<GetQueueQuery> {
    this.log("getQueue", { queueInput });
    return this.request<GetQueueQuery>(GetQueueDocument, { queueInput });
  }

  public async queueCount(): Promise<QueueCountQuery> {
    this.log("queueCount");
    return this.request<QueueCountQuery>(QueueCountDocument);
  }

  public async claimDesign(id: string): Promise<ClaimDesignMutation> {
    this.log("claimDesign", { id });
    return this.request<ClaimDesignMutation>(ClaimDesignDocument, { id });
  }

  public async claimNextDesign(): Promise<ClaimNextDesignMutation> {
    this.log("claimNextDesign");
    return this.request<ClaimNextDesignMutation>(ClaimNextDesignDocument);
  }

  public async requestRevision(input: RequestRevisionMutationVariables["input"]): Promise<RequestRevisionMutation> {
    this.log("requestRevision", { input });
    return this.request<RequestRevisionMutation>(RequestRevisionDocument, { input });
  }

  public async updatePriority({ id, priority }: UpdatePriorityMutationVariables): Promise<UpdatePriorityMutation> {
    this.log("updatePriority", { id, priority });
    return this.request<UpdatePriorityMutation>(UpdatePriorityDocument, { id, priority });
  }

  public async unassignDesignRequest(id: string): Promise<void> {
    this.log("unassignDesignRequest", { id });
    const { jwt } = new SessionClient();
    const response = await fetch(
      `${process.env.REACT_APP_VISION_API_URL}/design/v2/design-requests/${encodeURIComponent(id)}/unassign`,
      { method: "POST", headers: { Authorization: `Bearer ${jwt}` } },
    );
    if (response.ok) return;

    const message = await visionErrorMessage(response);
    throw new Error(message ?? `Couldn't unassign this design request (${response.status}).`);
  }
}

export const useVisionClient = () => {
  const client = React.useMemo(() => new VisionClient(), []);

  const getQueue = React.useCallback(
    async (take = 20, skip?: number, filter?: DesignRequestFindManyWhereInput) => {
      return client.getQueue({ take, skip, filter });
    },
    [client],
  );

  const queueCount = React.useCallback(async () => {
    return client.queueCount();
  }, [client]);

  const claimDesign = React.useCallback(
    async (id: string) => {
      return client.claimDesign(id);
    },
    [client],
  );

  const claimNextDesign = React.useCallback(async () => {
    return client.claimNextDesign();
  }, [client]);

  const updatePriority = React.useCallback(
    async (id: string, priority: number) => {
      return client.updatePriority({ id, priority });
    },
    [client],
  );
  const requestRevision = React.useCallback(
    async (input: RequestRevisionMutationVariables["input"]) => {
      return client.requestRevision(input);
    },
    [client],
  );

  const unassignDesign = React.useCallback(
    async (id: string) => {
      return client.unassignDesignRequest(id);
    },
    [client],
  );

  return {
    getQueue,
    queueCount,
    claimDesign,
    claimNextDesign,
    updatePriority,
    requestRevision,
    unassignDesign,
  };
};
